import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { getAllUsers, getAdminStats, generateQuestions, deleteUser, getTournamentLeaderboard } from '../utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Users, Crown, MessageSquare, BookOpen, UserPlus, TrendingUp, Award, RefreshCw, Trash2, BarChart3, Gift } from 'lucide-react';
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

export default function AdminDashboard({ user }) {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [sortBy, setSortBy] = useState('members'); // 'members', 'name', 'date'
  const [activeTab, setActiveTab] = useState('analytics'); // 'analytics', 'users', or 'tournament'
  const [tournamentPlayers, setTournamentPlayers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]); // For bulk delete
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [selectedTournamentPlayers, setSelectedTournamentPlayers] = useState([]); // For tournament bulk delete
  const [bulkDeletingTournament, setBulkDeletingTournament] = useState(false);

  useEffect(() => {
    console.log('🔍 AdminDashboard - Current user:', user);
    console.log('🔍 AdminDashboard - User role:', user?.role);
    
    if (!user || user.role !== 'admin') {
      console.error('❌ Admin access denied. User:', user, 'Role:', user?.role);
      toast.error(`Admin access required. Current role: ${user?.role || 'none'}`);
      navigate('/');
      return;
    }

    console.log('✅ Admin access granted');
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      const [usersResponse, statsResponse, tournamentResponse] = await Promise.all([
        getAllUsers(),
        getAdminStats(),
        getTournamentLeaderboard(),
      ]);

      setUsers(usersResponse.data.users || []);
      setStats(statsResponse.data);
      setTournamentPlayers(tournamentResponse.data.leaderboard || []);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateQuestions = async () => {
    setGenerating(true);
    try {
      const response = await generateQuestions();
      toast.success(response.data.message);
      loadData();
    } catch (error) {
      toast.error('Failed to generate questions');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateTournamentQuestions = async () => {
    setGenerating(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/questions/generate-tournament`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message + ` (${data.count} questions)`);
        loadData();
      } else {
        toast.error(data.detail || 'Failed to generate tournament questions');
      }
    } catch (error) {
      toast.error('Failed to generate tournament questions');
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteUser = async (userEmail) => {
    setDeleting(userEmail);
    try {
      await deleteUser(userEmail);
      toast.success(`User ${userEmail} deleted successfully`);
      loadData();
    } catch (error) {
      toast.error('Failed to delete user');
    } finally {
      setDeleting(null);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedUsers.length === 0) {
      toast.error('No users selected');
      return;
    }

    setBulkDeleting(true);
    try {
      let successCount = 0;
      let failCount = 0;

      for (const email of selectedUsers) {
        try {
          await deleteUser(email);
          successCount++;
        } catch (error) {
          failCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully deleted ${successCount} user(s)`);
      }
      if (failCount > 0) {
        toast.error(`Failed to delete ${failCount} user(s)`);
      }

      setSelectedUsers([]);
      loadData();
    } catch (error) {
      toast.error('Failed to delete users');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleSelectAll = () => {
    const nonAdminUsers = getSortedUsers().filter(u => u.role !== 'admin');
    if (selectedUsers.length === nonAdminUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(nonAdminUsers.map(u => u.email));
    }
  };

  const handleSelectUser = (email) => {
    if (selectedUsers.includes(email)) {
      setSelectedUsers(selectedUsers.filter(e => e !== email));
    } else {
      setSelectedUsers([...selectedUsers, email]);
    }
  };

  const handleDownloadCSV = () => {
    try {
      // Create CSV header
      const headers = ['Name', 'Email', 'Total Members', 'Best Score', 'Premium', 'Role', 'Created At'];
      
      // Create CSV rows
      const rows = users.map(u => [
        u.name || '',
        u.email || '',
        u.total_members || 0,
        u.best_score || 0,
        u.is_premium ? 'Yes' : 'No',
        u.role || 'user',
        u.created_at || ''
      ]);

      // Combine headers and rows
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `users_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`Exported ${users.length} users to CSV`);
    } catch (error) {
      toast.error('Failed to download CSV');
    }
  };

  const handleBulkDeleteTournament = async () => {
    if (selectedTournamentPlayers.length === 0) {
      toast.error('No players selected');
      return;
    }

    setBulkDeletingTournament(true);
    try {
      let successCount = 0;
      let failCount = 0;

      for (const email of selectedTournamentPlayers) {
        try {
          await deleteUser(email);
          successCount++;
        } catch (error) {
          failCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully deleted ${successCount} tournament player(s)`);
      }
      if (failCount > 0) {
        toast.error(`Failed to delete ${failCount} player(s)`);
      }

      setSelectedTournamentPlayers([]);
      loadData();
    } catch (error) {
      toast.error('Failed to delete players');
    } finally {
      setBulkDeletingTournament(false);
    }
  };

  const handleSelectAllTournament = () => {
    if (selectedTournamentPlayers.length === tournamentPlayers.length) {
      setSelectedTournamentPlayers([]);
    } else {
      setSelectedTournamentPlayers(tournamentPlayers.map(p => p.email));
    }
  };

  const handleSelectTournamentPlayer = (email) => {
    if (selectedTournamentPlayers.includes(email)) {
      setSelectedTournamentPlayers(selectedTournamentPlayers.filter(e => e !== email));
    } else {
      setSelectedTournamentPlayers([...selectedTournamentPlayers, email]);
    }
  };

  const getSortedUsers = () => {
    const usersCopy = [...users];
    switch (sortBy) {
      case 'members':
        return usersCopy.sort((a, b) => (b.total_members || 0) - (a.total_members || 0));
      case 'name':
        return usersCopy.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      case 'date':
        return usersCopy.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      default:
        return usersCopy;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#FFD700] via-[#4169E1] to-[#9370DB]">
        <div className="text-white text-2xl font-bold">Loading...</div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen py-12 px-6 relative"
      data-testid="admin-dashboard"
      style={{
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_mega-church-game/artifacts/d1c9p3vy_FreeVector-Modern-City-Vector-Art.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#4169E1]/40 via-[#9370DB]/40 to-[#FFD700]/30"></div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        <Button
          data-testid="back-btn"
          onClick={() => navigate('/')}
          variant="ghost"
          className="mb-6 text-white hover:bg-white/20"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>

        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_mega-church-game/artifacts/9qio0cok_Mega%20Church%20Logo%20T%20Shirt.png"
              alt="Mega Church Logo"
              className="w-20 h-20 object-contain"
            />
          </div>
          <h1 className="text-5xl sm:text-6xl font-bold text-white mb-4" data-testid="admin-title">Admin Dashboard</h1>
          <p className="text-xl text-white/90">Manage users and game content</p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center gap-4 mb-8">
          <Button
            onClick={() => setActiveTab('analytics')}
            className={`px-6 py-3 rounded-xl font-bold transition-all ${
              activeTab === 'analytics'
                ? 'bg-white text-[#4169E1] shadow-lg'
                : 'bg-white/20 text-white hover:bg-white/30'
            }`}
          >
            <BarChart3 className="w-5 h-5 mr-2" />
            Analytics
          </Button>
          <Button
            onClick={() => setActiveTab('users')}
            className={`px-6 py-3 rounded-xl font-bold transition-all ${
              activeTab === 'users'
                ? 'bg-white text-[#4169E1] shadow-lg'
                : 'bg-white/20 text-white hover:bg-white/30'
            }`}
          >
            <Users className="w-5 h-5 mr-2" />
            Users
          </Button>
          <Button
            onClick={() => setActiveTab('tournament')}
            className={`px-6 py-3 rounded-xl font-bold transition-all ${
              activeTab === 'tournament'
                ? 'bg-gradient-to-r from-red-600 to-green-600 text-white shadow-lg'
                : 'bg-white/20 text-white hover:bg-white/30'
            }`}
          >
            <Gift className="w-5 h-5 mr-2" />
            🎄 Tournament
          </Button>
        </div>

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="bg-white/95 backdrop-blur-lg rounded-3xl p-8">
            <AnalyticsDashboard />
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <>
            {/* Stats Cards */}
            {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <Card className="bg-white/95 backdrop-blur-lg p-6 rounded-2xl">
              <div className="flex items-center gap-4">
                <Users className="w-12 h-12 text-[#4169E1]" />
                <div>
                  <p className="text-3xl font-bold text-[#4169E1]" data-testid="total-users">{stats.total_users}</p>
                  <p className="text-gray-600">Total Users</p>
                </div>
              </div>
            </Card>

            <Card className="bg-white/95 backdrop-blur-lg p-6 rounded-2xl">
              <div className="flex items-center gap-4">
                <Crown className="w-12 h-12 text-yellow-500" />
                <div>
                  <p className="text-3xl font-bold text-yellow-500" data-testid="premium-users">{stats.premium_users}</p>
                  <p className="text-gray-600">Premium Users</p>
                </div>
              </div>
            </Card>

            <Card className="bg-white/95 backdrop-blur-lg p-6 rounded-2xl">
              <div className="flex items-center gap-4">
                <BookOpen className="w-12 h-12 text-purple-500" />
                <div>
                  <p className="text-3xl font-bold text-purple-500" data-testid="total-questions">{stats.total_questions}</p>
                  <p className="text-gray-600">Questions</p>
                </div>
              </div>
            </Card>

            <Card className="bg-white/95 backdrop-blur-lg p-6 rounded-2xl">
              <div className="flex items-center gap-4">
                <MessageSquare className="w-12 h-12 text-green-500" />
                <div>
                  <p className="text-3xl font-bold text-green-500" data-testid="total-suggestions">{stats.total_suggestions}</p>
                  <p className="text-gray-600">Suggestions</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Generate Questions */}
        <Card className="bg-white/95 backdrop-blur-lg p-8 rounded-3xl mb-8">
          <h2 className="text-2xl font-bold text-[#4169E1] mb-4">Question Management</h2>
          <p className="text-gray-600 mb-6">Generate AI-powered Bible trivia questions for all levels.</p>
          <div className="flex gap-4">
            <Button
              data-testid="generate-questions-btn"
              onClick={handleGenerateQuestions}
              disabled={generating}
              className="h-12 px-6 text-lg font-bold bg-[#4169E1] hover:bg-[#3558c7] rounded-xl"
            >
              {generating ? 'Generating...' : 'Generate Main Game Questions'}
            </Button>
            <Button
              data-testid="generate-tournament-questions-btn"
              onClick={handleGenerateTournamentQuestions}
              disabled={generating}
              className="h-12 px-6 text-lg font-bold bg-gradient-to-r from-red-600 to-green-600 hover:from-red-700 hover:to-green-700 text-white rounded-xl"
            >
              {generating ? 'Generating...' : '🎄 Generate Tournament Questions'}
            </Button>
          </div>
        </Card>

        {/* Users Table */}
        <Card className="bg-white/95 backdrop-blur-lg rounded-3xl overflow-hidden">
          <div className="p-8 border-b">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-[#4169E1]">All Users ({users.length})</h2>
              <div className="flex gap-2">
                <Button
                  onClick={handleDownloadCSV}
                  variant="outline"
                  className="flex items-center gap-2 border-green-600 text-green-600 hover:bg-green-50"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download CSV
                </Button>
                <Button
                  onClick={loadData}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </Button>
              </div>
            </div>
            
            {/* Bulk Actions */}
            {selectedUsers.length > 0 && (
              <div className="mb-4 p-4 bg-red-50 border-2 border-red-200 rounded-xl flex items-center justify-between">
                <span className="text-red-800 font-semibold">
                  {selectedUsers.length} user(s) selected
                </span>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={bulkDeleting}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      {bulkDeleting ? 'Deleting...' : `Delete ${selectedUsers.length} Selected`}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete {selectedUsers.length} Users?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete {selectedUsers.length} user(s)? This action cannot be undone and will remove all their game data.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleBulkDelete}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        Delete All
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}
            
            <div className="flex gap-2">
              <Button
                onClick={() => setSortBy('members')}
                variant={sortBy === 'members' ? 'default' : 'outline'}
                size="sm"
              >
                <TrendingUp className="w-4 h-4 mr-1" />
                By Members
              </Button>
              <Button
                onClick={() => setSortBy('name')}
                variant={sortBy === 'name' ? 'default' : 'outline'}
                size="sm"
              >
                By Name
              </Button>
              <Button
                onClick={() => setSortBy('date')}
                variant={sortBy === 'date' ? 'default' : 'outline'}
                size="sm"
              >
                By Date
              </Button>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="users-table">
              <thead className="bg-gradient-to-r from-[#4169E1] to-[#5B7FE8]">
                <tr>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-white">
                    <input
                      type="checkbox"
                      checked={selectedUsers.length === getSortedUsers().filter(u => u.role !== 'admin').length && selectedUsers.length > 0}
                      onChange={handleSelectAll}
                      className="w-4 h-4 cursor-pointer"
                    />
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-white">Name</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-white">Email</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-white">Members</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-white">Premium</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-white">Role</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-white">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {getSortedUsers().map((u, index) => (
                  <tr key={u.id || index} className="hover:bg-blue-50 transition-colors">
                    <td className="px-6 py-4 text-center">
                      {u.role !== 'admin' && (
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(u.email)}
                          onChange={() => handleSelectUser(u.email)}
                          className="w-4 h-4 cursor-pointer"
                        />
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{u.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{u.email}</td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex items-center gap-2">
                        <Award className="w-4 h-4 text-[#FFD700]" />
                        <span className="font-bold text-[#4169E1]">{u.total_members || 0}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {u.is_premium ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                          <Crown className="w-3 h-3 mr-1" />
                          Premium
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">
                          Free
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                        u.role === 'admin' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {u.role || 'user'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {u.role !== 'admin' && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              disabled={deleting === u.email}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete User?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete {u.name} ({u.email})? This action cannot be undone and will remove all their game data.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteUser(u.email)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

          </>
        )}

        {/* Tournament Tab */}
        {activeTab === 'tournament' && (
          <Card className="bg-white/95 backdrop-blur-lg rounded-3xl p-8 border-4 border-yellow-400">
            <div className="text-center mb-8">
              <div className="text-6xl mb-4">🎄</div>
              <h2 className="text-3xl font-bold bg-gradient-to-r from-red-600 via-green-600 to-red-600 bg-clip-text text-transparent mb-2">
                Christmas Tournament Leaderboard
              </h2>
              <p className="text-gray-600">
                Total Entries: {tournamentPlayers.length} | 1st: $100 | 2nd: $50
              </p>
            </div>

            {tournamentPlayers.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-xl text-gray-600">No tournament entries yet</p>
              </div>
            ) : (
              <>
                {/* Bulk Actions for Tournament */}
                {selectedTournamentPlayers.length > 0 && (
                  <div className="mb-4 p-4 bg-red-50 border-2 border-red-200 rounded-xl flex items-center justify-between">
                    <span className="text-red-800 font-semibold">
                      {selectedTournamentPlayers.length} player(s) selected
                    </span>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="destructive"
                          size="sm"
                          disabled={bulkDeletingTournament}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          {bulkDeletingTournament ? 'Deleting...' : `Delete ${selectedTournamentPlayers.length} Selected`}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete {selectedTournamentPlayers.length} Tournament Players?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete {selectedTournamentPlayers.length} tournament player(s)? This will remove their tournament entries, scores, and all progress.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={handleBulkDeleteTournament}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Delete All
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gradient-to-r from-red-600 via-green-600 to-red-600 text-white">
                        <th className="px-6 py-4 text-center rounded-tl-xl">
                          <input
                            type="checkbox"
                            checked={selectedTournamentPlayers.length === tournamentPlayers.length && selectedTournamentPlayers.length > 0}
                            onChange={handleSelectAllTournament}
                            className="w-4 h-4 cursor-pointer"
                          />
                        </th>
                        <th className="px-6 py-4 text-left">Rank</th>
                        <th className="px-6 py-4 text-left">Player</th>
                        <th className="px-6 py-4 text-left">Email</th>
                        <th className="px-6 py-4 text-right">Members</th>
                        <th className="px-6 py-4 text-center">Entry Date</th>
                        <th className="px-6 py-4 text-center rounded-tr-xl">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                    {tournamentPlayers.map((player, index) => {
                      const rank = index + 1;
                      const isWinner = rank <= 2;
                      
                      return (
                        <tr 
                          key={player.id || index}
                          className={`${isWinner ? 'bg-yellow-50' : 'hover:bg-gray-50'} transition-colors`}
                        >
                          <td className="px-6 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={selectedTournamentPlayers.includes(player.email)}
                              onChange={() => handleSelectTournamentPlayer(player.email)}
                              className="w-4 h-4 cursor-pointer"
                            />
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              {rank === 1 && <span className="text-2xl">🏆</span>}
                              {rank === 2 && <span className="text-2xl">🥈</span>}
                              <span className="font-bold text-lg">{rank}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              {player.picture ? (
                                <img 
                                  src={player.picture} 
                                  alt={player.name}
                                  className="w-10 h-10 rounded-full"
                                />
                              ) : (
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-600 to-green-600 flex items-center justify-center text-white font-bold">
                                  {player.name.charAt(0).toUpperCase()}
                                </div>
                              )}
                              <span className="font-semibold">{player.name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-gray-600">{player.email}</td>
                          <td className="px-6 py-4 text-right">
                            <span className="text-2xl font-bold text-green-600">
                              {player.tournament_members || 0}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-center text-gray-600">
                            {new Date(player.entry_date).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 text-center">
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  disabled={deleting === player.email}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Tournament Player?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete {player.name} ({player.email}) from the tournament? This will remove their tournament entry and all progress.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDeleteUser(player.email)}
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              </>
            )}

            <div className="mt-6 text-center">
              <Button
                onClick={loadData}
                variant="outline"
                className="px-6 py-3 border-2 border-green-600 text-green-600 hover:bg-green-50"
              >
                <RefreshCw className="w-5 h-5 mr-2" />
                Refresh
              </Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
